import {useGetEvents} from '@/features/agenda/queries/useGetEvents'
import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {ProjectWarningMissingEvaluationEventCompleted} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-evaluation-event-completed/ProjectWarningMissingEvaluationEventCompleted'
import {ProjectWarningMissingEventCompleted} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-event-completed/ProjectWarningMissingEventCompleted'
import {ProjectWarningMissingOpeningEvent} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-opening-event/ProjectWarningMissingOpeningEvent'
import {ProjectWarningMissingOpeningRelation} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-opening-relation/ProjectWarningMissingOpeningRelation'
import {ProjectWarningMissingTeamReport} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-team-report/ProjectWarningMissingTeamReport'
import {ProjectExtended} from '@/features/projects/types'
import {ProjectWarningMissingRenewalReport} from '../project-warning-missing-renewal-report/ProjectWarningMissingRenewalReport'
import {ProjectWarningMissingAssessmentReport} from '../project-warning-missing-assessment-report/ProjectWarningMissingAssessmentReport'
import dayjs from 'dayjs'
import {ProjectWarningMissingCloseReport} from '../project-warning-missing-close-report/ProjectWarningMissingCloseReport'
import {ProjectWarningMissingEvaluationEvent} from '../project-warning-missing-evaluation-event/ProjectWarningMissingEvaluationEvent'

/**
 * Displays warning banners for missing project actions based on project state
 *
 * This component analyzes a project's completion status and displays appropriate
 * warning banners to guide users on what actions need to be completed. The banners
 * follow a hierarchical priority system:
 *
 * 1. Initial evaluation event completion (highest priority)
 * 2. Opening event presence
 * 3. Opening event completion and report presence
 * 4. Opening relation creation
 * 5. Team report presence (lowest priority)
 *
 * @param project - The extended project object containing completion flags and metadata
 * @returns JSX.Element | null - The appropriate warning banner component or null if no warnings
 */
export const ProjectWarningMissingActionBanner = ({project}: {project: ProjectExtended}) => {
    const {data: patient, isLoading: isPatientPending} = useGetPatient({
        patientId: project.patientId,
        responseType: 'extended'
    })

    const {data: events, isLoading: isEventsPending} = useGetEvents({
        params: {
            projectId: project.id
        },
        options: {
            enabled:
                !project.isOpeningEventPresent ||
                !project.isOpeningEventCompleted ||
                !project.isOpeningReportPresent ||
                !project.isTeamReportPresent
        }
    })

    /**
     * Finds the initial evaluation event from the project's events
     * Initial evaluation events have access type 'initial_project_evaluation'
     */
    const initialEvaluation = events?.find(
        event =>
            event.eventConsultants?.[0]?.accessType?.accessType == 'initial_project_evaluation' ||
            event.eventConsultants?.[0]?.accessType?.accessType == 'observation_and_evaluation_sir'
    )
    const isInitialEvaluationEventPresent = !!initialEvaluation
    const isInitialEvaluationCompleted = initialEvaluation?.status == 'completed'

    /**
     * Finds the opening event from the project's events
     * Opening events have access type 'project_opening_processing' or 'cycle_opening_processing'
     */
    const openingEvent = events?.find(
        event =>
            event.eventConsultants?.[0]?.accessType?.accessType == 'project_opening_processing' ||
            event.eventConsultants?.[0]?.accessType?.accessType == 'cycle_opening_processing'
    )

    /**
     * Determines which warning banner to display based on project completion status
     *
     * Priority order:
     * 1. Initial evaluation event not completed (highest priority)
     * 2. Opening event missing
     * 3. Opening event not completed or opening report missing
     * 4. Opening relation missing (requires patient and project type)
     * 5. Team report missing (requires patient, project type, and opening report)
     *
     * @returns JSX.Element | null - The appropriate warning banner or null
     */
    const renderMissingBanner = () => {
        // const isEvolvedProject =
        //     !!project.renewedProjectId || !!project.variedProjectId || !!project.transformedProjectId

        const isMissingCloseReport =
            !project.renewedAt &&
            patient &&
            project.expirationDate &&
            dayjs(project.expirationDate).endOf('day') < dayjs() &&
            project.type

        if (
            isPatientPending ||
            isEventsPending
            // ||
            // (project.status != 'opening' &&
            //     !!project.sirId &&
            //     isEvolvedProject &&
            //     (project.isRenewContinuousProjectReportPresent || !isMissingCloseReport)
            // )
        )
            return null

        if (!!project.renewedProjectId && patient && !project.isRenewContinuousProjectReportPresent)
            return (
                <ProjectWarningMissingRenewalReport
                    projectId={project.id}
                    patient={patient}
                    expirationDate={project.expirationDate ? new Date(project.expirationDate) : new Date()}
                />
            )

        if (isMissingCloseReport)
            return (
                <ProjectWarningMissingCloseReport
                    projectId={project.id}
                    patient={patient}
                    projectType={project.type!} // field ensured in isMissingCloseReport
                    expirationDate={new Date(project.expirationDate!)} // field ensured in isMissingCloseReport
                />
            )

        if (isInitialEvaluationEventPresent && !isInitialEvaluationCompleted && initialEvaluation?.id)
            return <ProjectWarningMissingEvaluationEventCompleted eventId={initialEvaluation.id} />

        if (isInitialEvaluationEventPresent && patient && !project.isInitialProjectAssessmentReportPresent)
            return (
                <ProjectWarningMissingAssessmentReport
                    projectId={project.id}
                    patient={patient}
                    date={initialEvaluation.date ? new Date(initialEvaluation.date) : new Date()}
                />
            )

        if (!isInitialEvaluationEventPresent && project.status != 'closed') {
            if (project.projectRefId) {
                return <ProjectWarningMissingEvaluationEvent />
            }

            if (!project.isOpeningEventPresent) return <ProjectWarningMissingOpeningEvent />

            if (!project.isOpeningEventCompleted && openingEvent?.id)
                return <ProjectWarningMissingEventCompleted eventId={openingEvent.id} />

            if (!project.isOpeningReportPresent && patient && project.type && project.isOpeningEventPresent)
                return (
                    <ProjectWarningMissingOpeningRelation
                        projectId={project.id}
                        projectType={project.type}
                        patient={patient}
                    />
                )

            if (!project.isTeamReportPresent && patient && project.type && project.isOpeningReportPresent)
                return <ProjectWarningMissingTeamReport projectId={project.id} patient={patient} />
        }

        return null
    }

    return renderMissingBanner()
}
